import { maybe } from '@passionware/monads';
import { useRef } from 'react';
import { BooleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import { EnumFilter as EnumFilterType } from 'v5/api/_common/query/filters/EnumFilter';
import { GenericDateFilter } from 'v5/api/_common/query/filters/GenericDateFilter';
import { StringFilter } from 'v5/api/_common/query/filters/StringFilter';
import { BooleanFilterWidget } from './BooleanFilterWidget';
import { DateFilterWidget } from './DateFilterWidget';
import { EnumFilterWidget } from './EnumFilterWidget';
import { FilterWidgetProps, PerFieldConfigBase } from './FilterWidget.types';
import { FilterWidgetAddControl } from './FilterWidgetAddControl';
import { NumberFilterValue, NumberFilterWidget } from './NumberFilterWidget';
import { StringFilterWidget } from './StringFilterWidget';
import { UnsupportedFilterWidget } from './UnsupportedFilterWidget';
import { cn } from 'v5/platform/dom/cn';
import { uniq } from 'lodash';
import { Button } from 'v5/design-system/Button';

export function FilterWidget<FieldConfig extends PerFieldConfigBase>({
  config,
  order,
  groups = [],
  value,
  onValueChange,
  className,
  hiddenFilters = [],
  addFilterButtonRef,
  suggested = [],
  ...rest
}: FilterWidgetProps<FieldConfig>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const allFields = order ?? Object.keys(config);
  const displayOrder = allFields.filter(
    field => !hiddenFilters.includes(field)
  );

  // suggested fields keep their slots, in the host's order, ahead of the others
  const suggestedFields = uniq(suggested).filter(
    field => displayOrder.includes(field) && config[field]
  );
  const otherFields = uniq(displayOrder).filter(
    field => !suggestedFields.includes(field)
  );

  // Find all filters that are in value but don't have a config
  // These are filters that exist in the query but don't have a config definition
  const unsupportedFilters = Object.keys(value)
    .filter(
      field =>
        maybe.isPresent(value[field as keyof FieldConfig]) &&
        !hiddenFilters.includes(field) &&
        !displayOrder.includes(field) &&
        !config[field]
    )
    .map(field => field as keyof FieldConfig);

  const handleFocusNewFilter = (field: keyof FieldConfig) => {
    if (!containerRef.current) return;

    // Small delay to ensure the DOM is updated
    setTimeout(() => {
      // Find the filter widget root for this field
      const filterWidget = containerRef.current?.querySelector(
        `[data-filter-widget-field="${String(field)}"]`
      ) as HTMLElement;
      if (!filterWidget) return;

      // Find the value trigger button within this filter widget
      const valueTrigger = filterWidget.querySelector(
        `[data-element="value-trigger"]`
      ) as HTMLButtonElement;
      if (
        valueTrigger &&
        valueTrigger.getAttribute('aria-readonly') !== 'true' &&
        valueTrigger.hasAttribute('aria-expanded')
      ) {
        valueTrigger.focus();
        valueTrigger.click();
      } else {
        // Fallback to operator trigger if value trigger doesn't exist or is readonly
        const operatorTrigger = filterWidget.querySelector(
          `[data-element="operator-trigger"]`
        ) as HTMLButtonElement;
        if (
          operatorTrigger &&
          operatorTrigger.getAttribute('aria-readonly') !== 'true' &&
          operatorTrigger.hasAttribute('aria-expanded')
        ) {
          operatorTrigger.focus();
          operatorTrigger.click();
        }
      }
    }, 0);
  };

  const handleRemoveFilter = (field: keyof FieldConfig) => {
    // Get active filters before removal to determine which one to focus
    const activeFilters = displayOrder.filter(f => maybe.isPresent(value[f]));
    const currentIndex = activeFilters.indexOf(field);
    const hasPreviousFilter = currentIndex > 0;

    // Remove the filter
    onValueChange({ ...value, [field]: null });

    // Focus management after removal
    setTimeout(() => {
      if (hasPreviousFilter && containerRef.current) {
        // Focus the delete button of the previous filter
        // After removal, the previous filter's delete button will be at index (currentIndex - 1)
        const allDeleteButtons = containerRef.current.querySelectorAll(
          `[data-element="delete-trigger"]`
        );
        if (allDeleteButtons[currentIndex - 1]) {
          (allDeleteButtons[currentIndex - 1] as HTMLButtonElement).focus();
        }
      } else if (addFilterButtonRef?.current) {
        // No previous filter (this was the first one), focus the add filter button
        addFilterButtonRef.current.focus();
      }
    }, 0);
  };

  const renderWidget = (field: keyof FieldConfig) => {
    const itemConfig = config[field];
    if (maybe.isAbsent(value[field])) {
      return null;
    }
    // If config doesn't exist, render as unsupported
    if (!itemConfig) {
      return (
        <UnsupportedFilterWidget
          key={String(field)}
          filterName={String(field)}
          filterField={String(field)}
          value={value[field]}
          onRemove={() => handleRemoveFilter(field)}
        />
      );
    }
    // If config exists, render normally based on type
    switch (itemConfig.type) {
      case 'boolean':
        return (
          <BooleanFilterWidget
            key={String(field)}
            filterName={itemConfig.label}
            filterIcon={itemConfig.icon}
            filterField={String(field)}
            onRemove={() => handleRemoveFilter(field)}
            value={value[field] as unknown as BooleanFilter}
            onValueChange={next => onValueChange({ ...value, [field]: next })}
            valueLabel={itemConfig.options.valueLabel}
            subjectLabel={itemConfig.options.subjectLabel}
          />
        );
      case 'number':
        return (
          <NumberFilterWidget
            key={String(field)}
            filterName={itemConfig.label}
            filterIcon={itemConfig.icon}
            filterField={String(field)}
            onRemove={() => handleRemoveFilter(field)}
            value={value[field] as unknown as NumberFilterValue}
            onValueChange={next => onValueChange({ ...value, [field]: next })}
            valuePrefix={itemConfig.options.valuePrefix}
            valuePostfix={itemConfig.options.valuePostfix}
          />
        );
      case 'enum':
        return (
          <EnumFilterWidget
            key={String(field)}
            filterName={itemConfig.label}
            filterIcon={itemConfig.icon}
            filterField={String(field)}
            onRemove={() => handleRemoveFilter(field)}
            value={value[field] as unknown as EnumFilterType<any>}
            onValueChange={next => onValueChange({ ...value, [field]: next })}
            config={itemConfig.options}
            query={{ filters: value }}
          />
        );
      case 'date':
        return (
          <DateFilterWidget
            key={String(field)}
            filterName={itemConfig.label}
            filterIcon={itemConfig.icon}
            filterField={String(field)}
            onRemove={() => handleRemoveFilter(field)}
            value={value[field] as unknown as GenericDateFilter<any>}
            onValueChange={next => onValueChange({ ...value, [field]: next })}
            config={itemConfig.options}
          />
        );
      case 'string':
        return (
          <StringFilterWidget
            key={String(field)}
            filterName={itemConfig.label}
            filterIcon={itemConfig.icon}
            filterField={String(field)}
            onRemove={() => handleRemoveFilter(field)}
            value={value[field] as unknown as StringFilter}
            onValueChange={next => onValueChange({ ...value, [field]: next })}
            config={itemConfig.options}
          />
        );
      default:
        throw new Error(`Unknown filter type: ${(itemConfig as any).type}`);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-row flex-wrap gap-2', className)}
      {...rest}
    >
      <FilterWidgetAddControl
        config={config}
        order={displayOrder}
        groups={groups}
        value={value}
        onValueChange={onValueChange}
        hiddenFilters={hiddenFilters}
        onFilterAdded={handleFocusNewFilter}
        addFilterButtonRef={addFilterButtonRef}
        containerRef={containerRef}
      />
      {suggestedFields.map(field => {
        const itemConfig = config[field];
        if (maybe.isPresent(value[field])) {
          return renderWidget(field);
        }
        return (
          <Button
            key={String(field)}
            variant="secondaryMuted"
            size="sm"
            data-filter-suggestion={String(field)}
            disabled={itemConfig.disabled}
            onClick={() => {
              onValueChange({
                ...value,
                [field]: itemConfig.createDefaultValue()
              });
              handleFocusNewFilter(field);
            }}
          >
            {itemConfig.icon}
            {itemConfig.label}
          </Button>
        );
      })}
      {/* Use uniq to avoid rendering the same filter twice. Filter definitions can come from multiple groups, (for example same custom field for different resource types) so we need to avoid duplicates. */}
      {otherFields.map(renderWidget)}
      {unsupportedFilters.map(field => (
        <UnsupportedFilterWidget
          key={String(field)}
          filterName={String(field)}
          filterField={String(field)}
          filterIcon={undefined}
          value={value[field]}
          onRemove={() => handleRemoveFilter(field)}
        />
      ))}
    </div>
  );
}

export type {
  FilterWidgetProps,
  PerFieldConfigBase
} from './FilterWidget.types';
